import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { DataLakeQuery } from '@graylog/enterprise-api';

import type { MostRecentJobIds, ActivePreviewSearch } from 'data-lake/preview/Types';
import { PREVIEW_JOB_STATUS } from 'data-lake/preview/Constants';
import { defaultOnError } from 'util/conditional/onError';

const useJobProgress = (
  mostRecentJobIds: MostRecentJobIds,
  initialSearchJobStatus: ActivePreviewSearch['status'],
  executedSearchId: string,
): {
  data: {
    status: (typeof PREVIEW_JOB_STATUS)[keyof typeof PREVIEW_JOB_STATUS];
    progress: number;
  };
  setEnableProgressFetching: (val: boolean) => void;
} => {
  const [isQueryEnabled, setEnableProgressFetching] = useState(initialSearchJobStatus !== PREVIEW_JOB_STATUS.done);
  const fetchJobProgress = useCallback(async (searchId: string) => {
    const result = await DataLakeQuery.searchJobExecutionStateByjob_id(searchId);
    if (result.status !== PREVIEW_JOB_STATUS.running) {
      setEnableProgressFetching(false);
    }

    return result;
  }, []);
  const { data } = useQuery({
    queryKey: ['data-lake', 'progress', executedSearchId, mostRecentJobIds],
    queryFn: () =>
      defaultOnError(fetchJobProgress(mostRecentJobIds.asyncSearchId), 'Loading search progress failed with status'),
    refetchInterval: 200,
    gcTime: 0,
    enabled: !!(
      isQueryEnabled &&
      executedSearchId &&
      mostRecentJobIds?.asyncSearchId &&
      mostRecentJobIds?.searchId === executedSearchId
    ),
    initialData:
      initialSearchJobStatus === PREVIEW_JOB_STATUS.done
        ? { status: PREVIEW_JOB_STATUS.done, progress: 100 }
        : undefined,
  });

  return {
    data,
    setEnableProgressFetching: (newVal: boolean) => {
      setEnableProgressFetching(newVal);
    },
  };
};

export default useJobProgress;
